import React, { useState } from 'react'

import { HiOutlineXMark } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import Button from '@/components/common/Button'
import IconButton from '@/components/common/IconButtonV2'
import Link from '@/components/common/Link'
import { useEditorsInRoom } from '@/components/files/FileEditors/useEditorsInRoom'

import type { UUID } from '@/types/Id'

import { useViewerExpand } from '@/services/viewer-expand/context'

interface LatestVersionNoticeProps {
  fileId: UUID
  /** A version was saved after the document on screen was downloaded. */
  outdated: boolean
  /** Download the version that is the file now, in place of what is on screen. */
  onShowLatest: () => void
}

// A strip across the full width rather than a card, because the page underneath is
// white.
const BAR_CLASSNAME =
  'flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b px-3 py-1.5 text-sm'

// A row's height, so the strip lines up with the file list beside it at one line
// or two. Full screen hides that list, and a strip held to the height of rows
// that are not there is a band of empty colour above the page.
const ALIGNED_TO_ROWS_CLASSNAME = 'min-h-14'

/** Amber where the reader is only being told something, blue where there is something to do. */
const TONE_CLASSNAME = {
  editing:
    'border-amber-200 bg-amber-100 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950 dark:text-amber-100',
  saved:
    'border-blue-200 bg-blue-100 text-blue-900 dark:border-blue-900/60 dark:bg-blue-950 dark:text-blue-100',
}

/** An outline button's hover darkens its border, which on a strip with a colour of its own is easy to miss. */
const ACTION_CLASSNAME = 'hover:bg-primary-100 dark:hover:bg-primary-800'

/** The dismiss, tinted to the strip so it is not the one neutral thing on it. */
const DISMISS_CLASSNAME = {
  editing:
    'shrink-0 text-amber-800 hover:bg-amber-200 dark:text-amber-200 dark:hover:bg-amber-900',
  saved:
    'shrink-0 text-blue-800 hover:bg-blue-200 dark:text-blue-200 dark:hover:bg-blue-900',
}

/**
 * What was said, so dismissing sends that away and not whatever comes next. A boolean
 * rather than the editors: a card returning whenever anyone moved would be worse than
 * one that never came back.
 */
const saying = (fileId: UUID, editing: boolean, outdated: boolean): string =>
  `${fileId}|${editing}|${outdated}`

/**
 * Whether the reader is looking at the file as it stands, said only when they are not.
 * Nothing is drawn for the ordinary case; what is drawn can be sent away, and returns
 * when there is something else to say, such as a version landing while they read.
 */
export const LatestVersionNotice: React.FC<LatestVersionNoticeProps> = ({
  fileId,
  outdated,
  onShowLatest,
}) => {
  const editors = useEditorsInRoom({ fileId })
  const expand = useViewerExpand()
  const [dismissed, setDismissed] = useState<string | null>(null)

  const said = saying(fileId, editors !== null, outdated)
  if ((!editors && !outdated) || dismissed === said) {
    return null
  }

  // Somebody in the room outranks a landed version, so the colour follows the sentence.
  const tone = editors ? 'editing' : 'saved'

  return (
    <div
      role="status"
      className={twMerge(
        BAR_CLASSNAME,
        !expand?.expanded && ALIGNED_TO_ROWS_CLASSNAME,
        TONE_CLASSNAME[tone],
      )}
    >
      <span className="min-w-[12rem] flex-1">
        {editors
          ? `${editors.description} You're reading the last saved version.`
          : 'A new version of this file was saved.'}
      </span>
      <div className="ml-auto flex shrink-0 items-center gap-x-3">
        {outdated ? (
          <Button
            variant="outline-compact"
            onClick={onShowLatest}
            className={ACTION_CLASSNAME}
          >
            Show it
          </Button>
        ) : null}
        {editors ? (
          // A link, so the editor opens in a tab of its own when asked. Its own hover
          // dims, which washes the button out on a strip with a colour of its own.
          <Link href={editors.href} className="hover:opacity-100">
            <Button variant="outline-compact" className={ACTION_CLASSNAME}>
              Open in the editor
            </Button>
          </Link>
        ) : null}
        <IconButton
          tooltip="Dismiss"
          aria-label="Dismiss"
          onClick={() => setDismissed(said)}
          className={DISMISS_CLASSNAME[tone]}
        >
          <HiOutlineXMark size={16} />
        </IconButton>
      </div>
    </div>
  )
}
